import { useState, useCallback, useEffect } from 'react';
import { Text, TextField, Box, InlineStack, RadioButton, Card, Select, Checkbox, ChoiceList, FormLayout, Form, BlockStack, Grid } from '@shopify/polaris';
import { PROGRAM_CONSTANTS } from '#client/pages/Programs/constants/programsConstants';
import { useProgramActions, useProgramDetails } from '#client/stores/program/useProgramStore';
import { useProductFilterStore } from '#client/stores/productFilter/useProductFilterStore';

const ProgramDetails = () => {
    // Zustand store hooks
    const {
        mode,
        discountAmount,
        discountType,
        subsidyType,
        maxItems,
        maxValue,
        maxUse,
        externallyManaged,
        firstNameRequired,
        lastNameRequired,
        employeeIdRequired,
        poNumberRequired,
        programDescription,
        hasActiveVouchers,
        errors = {},
    } = useProgramDetails();

    const blockExternallyManaged = useProductFilterStore.use.conditionType() === 'groupedRules' && !externallyManaged;

    const {
        setDiscountAmount,
        setDiscountType,
        setSubsidyType,
        setMaxItems,
        setMaxUse,
        setMaxValue,
        setExternallyManaged,
        setFirstNameRequired,
        setLastNameRequired,
        setEmployeeIdRequired,
        setPoNumberRequired,
        setProgramDescription,
    } = useProgramActions();

    const [showEligibleProducts, setShowEligibleProducts] = useState('show');

    // State for required fields
    const [requiredFields, setRequiredFields] = useState([]);

    // Initialize required fields based on the current state
    useEffect(() => {
        const initialRequiredFields = [];
        if (firstNameRequired) initialRequiredFields.push(PROGRAM_CONSTANTS.REQUIRED_FIELDS.FIRST_NAME);
        if (lastNameRequired) initialRequiredFields.push(PROGRAM_CONSTANTS.REQUIRED_FIELDS.LAST_NAME);
        if (employeeIdRequired) initialRequiredFields.push(PROGRAM_CONSTANTS.REQUIRED_FIELDS.EMPLOYEE_ID);
        if (poNumberRequired) initialRequiredFields.push(PROGRAM_CONSTANTS.REQUIRED_FIELDS.PO_NUMBER);
        setRequiredFields(initialRequiredFields);
    }, [firstNameRequired, lastNameRequired, employeeIdRequired, poNumberRequired]);

    // Validation handlers for numeric fields
    const handleNumericChange = useCallback((value, onChange) => {
        if (value === '' || PROGRAM_CONSTANTS.DECIMAL_REGEX.test(value)) {
            onChange(value);
        }
    }, []);

    const handleDiscountAmountChange = useCallback(
        (value) => {
            handleNumericChange(value, setDiscountAmount);
        },
        [handleNumericChange, setDiscountAmount]
    );

    const handleMaxValueChange = useCallback(
        (value) => {
            handleNumericChange(value, setMaxValue);
        },
        [handleNumericChange, setMaxValue]
    );

    const handleMaxItemsChange = useCallback(
        (value) => {
            handleNumericChange(value, setMaxItems);
        },
        [handleNumericChange, setMaxItems]
    );

    const handleMaxUseChange = useCallback(
        (value) => {
            handleNumericChange(value, setMaxUse);
        },
        [handleNumericChange, setMaxUse]
    );

    const handleRequiredFieldsChange = (value) => {
        setRequiredFields(value);
        setFirstNameRequired(value.includes(PROGRAM_CONSTANTS.REQUIRED_FIELDS.FIRST_NAME));
        setLastNameRequired(value.includes(PROGRAM_CONSTANTS.REQUIRED_FIELDS.LAST_NAME));
        setEmployeeIdRequired(value.includes(PROGRAM_CONSTANTS.REQUIRED_FIELDS.EMPLOYEE_ID));
        setPoNumberRequired(value.includes(PROGRAM_CONSTANTS.REQUIRED_FIELDS.PO_NUMBER));
    };

    const getSubsidyLabel = () => {
        switch (subsidyType) {
            case PROGRAM_CONSTANTS.SUBSIDY_TYPES.TOTAL_VALUE:
                return 'Total Value';
            case PROGRAM_CONSTANTS.SUBSIDY_TYPES.PERCENTAGE_SUBSIDY:
                return 'Subsidy %';
            default:
                return 'Subsidy Value';
        }
    };

    const viewStatusMarkup = (
        <Box paddingBlock="400">
            <Grid>
                <Grid.Cell columnSpan={{ xs: 12, sm: 3, md: 3, lg: 4, xl: 4 }}>
                    <BlockStack gap="200">
                        {subsidyType && subsidyType !== '' && (
                            <Text as="span">
                                <Text variant="headingSm" as="span">
                                    Subsidy Type:
                                </Text>{' '}
                                <Text as="span">{getSubsidyLabel()}</Text>
                            </Text>
                        )}
                        {discountType && discountType !== 'none' && (
                            <Text as="span">
                                <Text variant="headingSm" as="span">
                                    Discount Type:
                                </Text>{' '}
                                <Text as="span">{discountType === 'none' ? 'None' : discountType}</Text>
                            </Text>
                        )}
                        <Text as="span">
                            <Text variant="headingSm" as="span">
                                Program Description:
                            </Text>{' '}
                            <Text as="span">{programDescription}</Text>
                        </Text>
                        <Text as="span">
                            <Text variant="headingSm" as="span">
                                Eligible Products in Voucher View:
                            </Text>{' '}
                            <Text as="span">{showEligibleProducts === 'show' ? 'Show' : 'Hide'}</Text>
                        </Text>
                    </BlockStack>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 12, sm: 3, md: 3, lg: 4, xl: 4 }}>
                    <BlockStack gap="200">
                        {subsidyType && subsidyType !== '' && (
                            <Text as="span">
                                <Text variant="headingSm" as="span">
                                    Subsidy Value:
                                </Text>{' '}
                                <Text as="span">{maxValue || ''}</Text>
                            </Text>
                        )}
                        {discountAmount && discountAmount !== '' && (
                            <Text as="span">
                                <Text variant="headingSm" as="span">
                                    Discount Amount:
                                </Text>{' '}
                                <Text as="span">{discountAmount || ''}</Text>
                            </Text>
                        )}
                        {externallyManaged ? (
                            <>
                                <Text as="span">
                                    <Text variant="headingSm" as="span">
                                        Externally Managed:
                                    </Text>{' '}
                                    <Text as="span">{externallyManaged ? 'Yes' : 'No'}</Text>
                                </Text>
                                <Text as="span">
                                    <Text variant="headingSm" as="span">
                                        First Name:
                                    </Text>{' '}
                                    <Text as="span">{firstNameRequired ? 'Yes' : 'No'}</Text>
                                </Text>
                                <Text as="span">
                                    <Text variant="headingSm" as="span">
                                        Last Name:
                                    </Text>{' '}
                                    <Text as="span">{lastNameRequired ? 'Yes' : 'No'}</Text>
                                </Text>
                                <Text as="span">
                                    <Text variant="headingSm" as="span">
                                        Employee ID:
                                    </Text>{' '}
                                    <Text as="span">{employeeIdRequired ? 'Yes' : 'No'}</Text>
                                </Text>
                                <Text as="span">
                                    <Text variant="headingSm" as="span">
                                        PO#:
                                    </Text>{' '}
                                    <Text as="span">{poNumberRequired ? 'Yes' : 'No'}</Text>
                                </Text>
                            </>
                        ) : (
                            <Text as="span">
                                <Text variant="headingSm" as="span">
                                    Externally Managed:
                                </Text>{' '}
                                <Text as="span">No</Text>
                            </Text>
                        )}
                    </BlockStack>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 12, sm: 3, md: 3, lg: 4, xl: 4 }}>
                    <BlockStack gap="200">
                        <Text as="span">
                            <Text variant="headingSm" as="span">
                                Max # of Items:
                            </Text>{' '}
                            <Text as="span">{maxItems || '0'}</Text>
                        </Text>
                        <Text as="span">
                            <Text variant="headingSm" as="span">
                                Max Uses:
                            </Text>{' '}
                            <Text as="span">{maxUse || '0'}</Text>
                        </Text>
                    </BlockStack>
                </Grid.Cell>
            </Grid>
        </Box>
    );

    const editCreateStatusMarkup = (
        <Form>
            <FormLayout>
                <FormLayout.Group>
                    <Select
                        label="Subsidy Type"
                        options={[
                            { label: 'Select a subsidy type', value: PROGRAM_CONSTANTS.SUBSIDY_TYPES.SELECT_SUBSIDY_TYPE },
                            { label: 'Total Value', value: PROGRAM_CONSTANTS.SUBSIDY_TYPES.TOTAL_VALUE },
                            { label: '% Subsidy', value: PROGRAM_CONSTANTS.SUBSIDY_TYPES.PERCENTAGE_SUBSIDY },
                        ]}
                        onChange={setSubsidyType}
                        value={subsidyType || ''}
                        disabled={mode === 'edit' && hasActiveVouchers}
                        helpText={mode === 'edit' && hasActiveVouchers ? 'Cannot change subsidy type when program has active vouchers' : undefined}
                    />
                    <TextField
                        label={getSubsidyLabel()}
                        type="text"
                        inputMode="numeric"
                        value={maxValue || ''}
                        onChange={handleMaxValueChange}
                        error={errors.maxValue}
                        disabled={!subsidyType || subsidyType === ''}
                    />
                </FormLayout.Group>
                <FormLayout.Group>
                    <Select
                        label="Discount Type"
                        options={[
                            { label: 'Select a discount type', value: PROGRAM_CONSTANTS.DISCOUNT_TYPES.SELECT_DISCOUNT_TYPE },
                            { label: 'None', value: PROGRAM_CONSTANTS.DISCOUNT_TYPES.NONE },
                            { label: '% Off', value: PROGRAM_CONSTANTS.DISCOUNT_TYPES.PERCENTAGE },
                            { label: 'Flat Discount', value: PROGRAM_CONSTANTS.DISCOUNT_TYPES.FIXED_AMOUNT },
                        ]}
                        onChange={setDiscountType}
                        value={discountType || ''}
                    />
                    <TextField
                        label="Discount Amount"
                        type="text"
                        inputMode="numeric"
                        value={discountAmount || ''}
                        onChange={handleDiscountAmountChange}
                        disabled={!discountType || discountType === 'none'}
                        error={errors.discountAmount}
                    />
                </FormLayout.Group>
                <FormLayout.Group>
                    <Box>
                        <TextField label="Max # of Items" type="text" inputMode="numeric" value={maxItems || ''} onChange={handleMaxItemsChange} autoComplete="off" error={errors.maxItems} />
                        <Box maxWidth="300px" paddingBlockStart="400" paddingBlockEnd="800">
                            <TextField
                                label="Description"
                                multiline={6}
                                autoComplete="off"
                                value={programDescription}
                                onChange={setProgramDescription}
                                maxLength={500}
                                showCharacterCount
                                error={errors.programDescription}
                                requiredIndicator
                            />
                        </Box>
                        <InlineStack wrap={false} gap="200" blockAlign="center">
                            <Text>Eligible Products in Voucher View</Text>
                            <RadioButton checked={showEligibleProducts === 'show'} id="show" label="Show" onChange={() => setShowEligibleProducts('show')} />
                            <RadioButton checked={showEligibleProducts === 'hide'} id="hide" label="Hide" onChange={() => setShowEligibleProducts('hide')} />
                        </InlineStack>
                    </Box>
                    <Box>
                        <Box paddingBlockEnd="400">
                            <TextField label="Max Uses" type="text" inputMode="numeric" value={maxUse || ''} onChange={handleMaxUseChange} autoComplete="off" error={errors.maxUse} />
                        </Box>
                        <Box paddingBlockEnd="400">
                            <Checkbox
                                label="Externally Managed"
                                checked={externallyManaged}
                                onChange={setExternallyManaged}
                                // Externally managed programs don't get grouped rules, so a grouped program can't switch over
                                disabled={blockExternallyManaged}
                                helpText={
                                    blockExternallyManaged ? "Externally managed programs can't use grouped rules. Change Products must match to All Conditions or Any Conditions first." : undefined
                                }
                                error={errors.externallyManaged}
                            />
                        </Box>
                        {externallyManaged && (
                            <ChoiceList
                                allowMultiple
                                title="Required Fields"
                                choices={[
                                    {
                                        label: 'First Name',
                                        value: PROGRAM_CONSTANTS.REQUIRED_FIELDS.FIRST_NAME,
                                    },
                                    {
                                        label: 'Last Name',
                                        value: PROGRAM_CONSTANTS.REQUIRED_FIELDS.LAST_NAME,
                                    },
                                    {
                                        label: 'Employee ID',
                                        value: PROGRAM_CONSTANTS.REQUIRED_FIELDS.EMPLOYEE_ID,
                                    },
                                    {
                                        label: 'PO#',
                                        value: PROGRAM_CONSTANTS.REQUIRED_FIELDS.PO_NUMBER,
                                    },
                                ]}
                                selected={requiredFields}
                                onChange={handleRequiredFieldsChange}
                                error={errors.requiredFields}
                            />
                        )}
                    </Box>
                </FormLayout.Group>
            </FormLayout>
        </Form>
    );

    return (
        <Card>
            <Box paddingBlock="400">
                <Text variant="headingLg" as="h2">
                    Program Details
                </Text>
            </Box>
            {mode === 'view' ? viewStatusMarkup : editCreateStatusMarkup}
        </Card>
    );
};

export default ProgramDetails;
